import { useEffect, useState, useMemo, useRef } from 'react';
import { Search, Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { supabase } from '../../lib/supabase';

/**
 * BankAccountFields — Paystack-backed payment-detail picker used by organizers
 * when setting up a paid event.
 *
 * 1. Loads the Nigerian bank list once via the paystack-banks Edge Function
 * 2. Bank picker is a searchable combobox (type to filter)
 * 3. As soon as both bank + 10-digit account number are present, it auto-
 *    resolves the account name and writes it back via onChange.
 *
 * Props:
 *   bankName, accountNumber, accountName — controlled string values
 *   onChange — partial setter: ({ bankName?, accountNumber?, accountName? }) => void
 */
export function BankAccountFields({ bankName, accountNumber, accountName, onChange }) {
  const [banks, setBanks] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [bankQuery, setBankQuery] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);

  // Track the (accountNumber, bankCode) pair we last resolved so we don't
  // re-fetch on every keystroke.
  const lastResolvedRef = useRef('');

  // Look up bank code from the chosen name (we keep `bankName` as the public
  // shape since that's what the events table stores).
  const selectedBank = useMemo(
    () => banks.find((b) => b.name === bankName) || null,
    [banks, bankName],
  );

  // Fetch banks once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBankLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('paystack-banks', { method: 'GET' });
        if (cancelled) return;
        if (error) throw error;
        setBanks(Array.isArray(data?.banks) ? data.banks : []);
      } catch (e) {
        console.warn('Bank list load failed:', e?.message);
      } finally {
        if (!cancelled) setBankLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-resolve account name once we have bankCode + 10-digit account number.
  useEffect(() => {
    if (!selectedBank?.code || !accountNumber || accountNumber.length !== 10) return;
    const key = `${selectedBank.code}:${accountNumber}`;
    if (lastResolvedRef.current === key) return;
    lastResolvedRef.current = key;

    setResolveError(null);
    setResolving(true);
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('paystack-banks', {
          body: { bankCode: selectedBank.code, accountNumber },
        });
        if (cancelled) return;
        if (error || !data?.ok) throw new Error(error?.message || data?.error || 'Could not resolve');
        onChange({ accountName: data.accountName });
      } catch (e) {
        if (cancelled) return;
        setResolveError(e?.message || 'Could not verify account');
        onChange({ accountName: '' });
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBank?.code, accountNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = bankQuery.trim().toLowerCase();
  const filteredBanks = q ? banks.filter((b) => b.name.toLowerCase().includes(q)) : banks;

  return (
    <div className="space-y-3" data-testid="bank-account-fields">
      <div>
        <Label className="rivl-label text-[10px]">Bank</Label>
        <Popover open={bankOpen} onOpenChange={setBankOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={bankOpen}
              data-testid="bank-picker-trigger"
              className="w-full h-11 justify-between font-normal text-base rounded-md"
            >
              {bankLoading ? (
                <span className="text-muted-foreground inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading banks…
                </span>
              ) : (
                <span className={bankName ? '' : 'text-muted-foreground'}>
                  {bankName || 'Select bank'}
                </span>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[min(360px,90vw)] p-0 max-h-[60vh] overflow-hidden"
            data-testid="bank-picker-popover"
          >
            <div className="relative border-b border-border">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                type="text"
                placeholder="Search banks…"
                value={bankQuery}
                onChange={(e) => setBankQuery(e.target.value)}
                data-testid="bank-picker-search"
                className="w-full h-10 pl-9 pr-3 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            <div className="overflow-y-auto max-h-[48vh]" role="listbox">
              {filteredBanks.length === 0 && (
                <p className="px-3 py-6 text-sm text-muted-foreground text-center">No match.</p>
              )}
              {filteredBanks.map((b) => (
                <button
                  key={b.code}
                  type="button"
                  role="option"
                  aria-selected={bankName === b.name}
                  onClick={() => {
                    onChange({ bankName: b.name, accountName: '' });
                    lastResolvedRef.current = '';
                    setBankQuery('');
                    setBankOpen(false);
                  }}
                  data-testid={`bank-option-${b.code}`}
                  className={`w-full px-3 h-10 flex items-center gap-2 text-left text-sm hover:bg-secondary/60 transition-colors ${
                    bankName === b.name ? 'bg-secondary/40 font-medium' : ''
                  }`}
                >
                  <Check className={`h-4 w-4 ${bankName === b.name ? 'opacity-100 text-primary' : 'opacity-0'}`} />
                  <span className="truncate">{b.name}</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="rivl-label text-[10px]">Account number</Label>
          <Input
            data-testid="ce-account-number"
            value={accountNumber || ''}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 10);
              onChange({ accountNumber: v, accountName: '' });
            }}
            placeholder="0123456789"
            inputMode="numeric"
            className="h-11 rounded-md font-mono tracking-wider"
          />
        </div>
        <div>
          <Label className="rivl-label text-[10px]">
            Account name
            {resolving && <Loader2 className="inline-block h-3 w-3 ml-1 animate-spin text-muted-foreground" />}
          </Label>
          <Input
            data-testid="ce-account-name"
            value={accountName || ''}
            readOnly
            placeholder={resolving ? 'Verifying…' : 'Auto-filled after lookup'}
            className="h-11 rounded-md bg-secondary/30 cursor-default"
          />
        </div>
      </div>
      {resolveError && (
        <p className="text-[11px] text-destructive" data-testid="bank-resolve-error">{resolveError}</p>
      )}
    </div>
  );
}
